import { Injectable, Injector } from '@angular/core';
import { AjaxStorageDriver } from 'src/app/drivers/io/ajax-storage-driver';
import { ExampleDriver } from 'src/app/drivers/io/example-driver';
import { FirebaseStorageDriver } from 'src/app/drivers/io/firebase-storage-driver';
import { IFile } from 'src/app/interfaces/io/file.interface';
import { ShellService } from '../process/shell.service';
import { IFileDriver } from './drivers/file-driver.interface';
import { Drive } from './drives/drive';

@Injectable({
  providedIn: 'any'
})
export class VfmService {
  private static drives: Drive[] = [];

  constructor(private injector: Injector) {
  }

  private get nextFreeUnit(): string {
    const available = "CDEFGHIJKLMNOPQRSTUVWXYZ";
    return available[VfmService.drives.length];
  }

  get units(): string[] {
    return VfmService.drives.map(m => m.unit);
  }

  get allDrives() {
    return VfmService.drives;
  }

  async load() {
    this.addDrive(new ExampleDriver(), "/assets/data/io/json-driver.json");
    this.addDrive(new FirebaseStorageDriver(), "/assets/data/io/firebase-driver.json");
    this.addDrive(new AjaxStorageDriver(), "/assets/data/io/ajax-driver.json");
    await VfmService.drives[0].mount("C:", this.injector);
    this.callMountEvents(VfmService.drives[0]);
  }

  async mount(unit: string) {
    const drive = this.getDrive(unit);
    await drive.mount(unit, this.injector);
    this.callMountEvents(drive);
  }

  private async callMountEvents(drive: Drive) {
    const existsEventsFolder = await this.getDriver(drive.unit).exists("/system/$events/mount");
    if (!existsEventsFolder) {
      return;
    }

    const files = await this.getDriver(drive.unit).list("/system/$events/mount");
    for (const file of files) {
      console.log("Startup: starting", file);
      await this.injector.get(ShellService).launch(file);
    }
  }

  exists(path: string): Promise<boolean> {
    const driver = this.getDriver(this.extractUnit(path));
    return driver.exists(path);
  }

  rmdir(path: string): Promise<void> {
    const driver = this.getDriver(this.extractUnit(path));
    return driver.rmdir(path);
  }

  unlink(path: string): Promise<void> {
    const driver = this.getDriver(this.extractUnit(path));
    return driver.unlink(path);
  }

  writeTextFile(path: string, content: string): Promise<void> {
    const driver = this.getDriver(this.extractUnit(path));
    return driver.writeTextFile(path, content);
  }

  readTextFile(path: string): Promise<string> {
    const driver = this.getDriver(this.extractUnit(path));
    return driver.readTextFile(path);
  }

  isDirectory(file: IFile) {
    return file && file.properties && file.properties.isDirectory;
  }

  list(path: string): Promise<IFile[]> {
    const driver = this.getDriver(this.extractUnit(path));
    return driver.list(path);
  }

  private addDrive(driver: IFileDriver, settings: string) {
    const unit = this.nextFreeUnit;
    const drive = new Drive(settings, {
      unit,
      driver,
      name: driver.getDriveType()
    });

    VfmService.drives.push(drive);

    return drive;
  }

  private getDriver(unit: string) {
    const drive = this.getDrive(unit);
    if (!drive) {
      throw new Error(`The drive ${unit} doesn't exists.`);
    }

    if (!drive.isMounted) {
      throw new Error(`The drive ${unit} is not mounted.`);
    }

    return drive.driver;
  }

  getDrive(unit: string) {
    return VfmService.drives.find(m => m.unit === unit);
  }

  extractUnit(dir: string): string {
    if (!VfmService.drives.length) {
      return null;
    }

    if (!dir.includes(":")) {
      return VfmService.drives[0].unit;
    }

    return dir.substr(0, dir.indexOf(":"));
  }
}
